import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import Header from "./Header";
import VertNav from "./VertNav";
import config from "../../config";

export default function VendorSettingsPage() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [vendorForm, setVendorForm] = useState({
        name: "",
        address: "",
        phone: "",
        email: "",
    });
    const [vendorData, setVendorData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const vendorsPerPage = 10; // fixed page size

    const totalPages = Math.ceil(vendorData.length / vendorsPerPage);

    const bid = localStorage.getItem("branch_id");

    const fetchVendors = async () => {
        try {
            const response = await fetch(
                `${config.apiUrl}/api/swalook/vendor/expense-purchase/?branch_name=${bid}`,
                {
                    headers: {
                        Authorization: `Token ${localStorage.getItem("token")}`,
                    },
                }
            );
            const result = await response.json();

            console.log("API result:", result);

            // ✅ Correct extraction from API shape
            const vendors = Array.isArray(result.data) ? result.data : [];

            setVendorData(vendors);
        } catch (error) {
            console.error("Failed to fetch vendors:", error);
            toast.error("Error fetching vendor data!");
        }
    };

    useEffect(() => {
        fetchVendors();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setVendorForm({ ...vendorForm, [name]: value });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch(
                `${config.apiUrl}/api/swalook/vendor/expense-purchase/?branch_name=${bid}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Token ${localStorage.getItem("token")}`,
                    },
                    body: JSON.stringify({
                        vendor_name: vendorForm.name,
                        vendor_address: vendorForm.address,
                        vendor_mobile_no: vendorForm.phone,
                        vendor_email: vendorForm.email,
                        branch_name: bid,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to add vendor");
            }

            toast.success("Vendor added successfully!");
            setVendorForm({ name: "", address: "", phone: "", email: "" });
            setIsModalOpen(false);
            fetchVendors();
        } catch (error) {
            console.error("Error adding vendor:", error);
            toast.error("Failed to add vendor. Try again.");
        }
    };

    const handlePageChange = (page) => setCurrentPage(page);

    const renderPaginationNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let end = Math.min(totalPages, start + maxVisiblePages - 1);

        if (end - start + 1 < maxVisiblePages) {
            start = Math.max(1, end - maxVisiblePages + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`w-8 h-8 flex items-center justify-center text-sm font-medium rounded-full ${
                        i === currentPage
                            ? "bg-blue-500 text-white"
                            : "text-gray-600 hover:bg-gray-200"
                    }`}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    return (
        <>
            <Header />
            <VertNav />
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="min-h-screen bg-white p-4 md:p-8 md:ml-72">
                <div className="w-full flex justify-between items-center mb-6 p-4 mt-8">
                    <h1 className="text-2xl font-semibold text-gray-800">
                        Vendor Settings:
                    </h1>
                    <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full flex items-center gap-2 transition"
                        onClick={() => setIsModalOpen(true)}
                    >
                        <Plus size={16} />
                        New Vendor
                    </button>
                </div>

                {/* Vendor Table */}
                <div className="border border-gray-200 rounded-[2.5rem] shadow-md overflow-hidden ">
                    <div className="bg-gray-100 border-b px-6 py-4 grid grid-cols-6 font-medium text-sm text-gray-600">
                        <div>S. No.</div>
                        <div>Date Added</div>
                        <div>Mobile No.</div>
                        <div>Name</div>
                        <div>Total Purchases</div>
                        <div>Total Expenses</div>
                    </div>
                    <div className="divide-y divide-gray-100 bg-white">
                        {Array.isArray(vendorData) && vendorData.length > 0 ? (
                            vendorData
                                .slice(
                                    (currentPage - 1) * vendorsPerPage,
                                    currentPage * vendorsPerPage
                                )
                                .map((item, idx) => (
                                    <div
                                        key={
                                            (currentPage - 1) * vendorsPerPage +
                                            idx
                                        }
                                        className="grid grid-cols-6 px-6 py-4 text-sm text-gray-800 hover:bg-gray-50 transition"
                                    >
                                        <div>
                                            {(currentPage - 1) *
                                                vendorsPerPage +
                                                idx +
                                                1}
                                        </div>
                                        <div>—</div>
                                        <div>{item.vendor_mobile_no}</div>
                                        <div>{item.vendor_name}</div>
                                        <div>{item.total_purchase ?? "—"}</div>
                                        <div>{item.total_expense ?? "—"}</div>
                                    </div>
                                ))
                        ) : (
                            <div className="col-span-5 text-center py-4 text-gray-500">
                                No vendors found
                            </div>
                        )}
                    </div>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-6 ">
                    <div className="text-sm text-gray-600">
                        Showing {(currentPage - 1) * vendorsPerPage + 1}–
                        {Math.min(
                            currentPage * vendorsPerPage,
                            vendorData.length
                        )}{" "}
                        of {vendorData.length} rows
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        {renderPaginationNumbers()}
                        {currentPage < totalPages - 2 && (
                            <>
                                <span className="text-gray-400">...</span>
                                <button
                                    onClick={() => handlePageChange(totalPages)}
                                    className="w-8 h-8 flex items-center justify-center text-sm text-gray-600 hover:bg-gray-200 rounded-full"
                                >
                                    {totalPages}
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Add Vendor Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
                    <div className="bg-white w-full max-w-md mx-auto p-6 rounded-2xl shadow-xl relative">
                        <button
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"
                            onClick={() => setIsModalOpen(false)}
                        >
                            <X size={20} />
                        </button>
                        <h2 className="text-lg font-semibold mb-4 text-gray-800">
                            Add New Vendor
                        </h2>
                        <form onSubmit={handleFormSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Vendor Name *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    value={vendorForm.name}
                                    onChange={handleInputChange}
                                    className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Vendor Address *
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    required
                                    value={vendorForm.address}
                                    onChange={handleInputChange}
                                    className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Phone Number *
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    required
                                    value={vendorForm.phone}
                                    onChange={handleInputChange}
                                    className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Email ID (Optional)
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={vendorForm.email}
                                    onChange={handleInputChange}
                                    className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-full transition"
                            >
                                Submit
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
